import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, IndianRupee, Plus, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  pending:      { bg: "bg-amber-50",    text: "text-amber-800",    label: "Pending approval", icon: Clock },
  processing:   { bg: "bg-blue-50",     text: "text-blue-800",     label: "Processing",       icon: Loader2 },
  processed:    { bg: "bg-emerald-50",  text: "text-emerald-800",  label: "Processed",        icon: CheckCircle },
  auto_approved:{ bg: "bg-emerald-50",  text: "text-emerald-800",  label: "Auto-approved",    icon: CheckCircle },
  rejected:     { bg: "bg-red-50",      text: "text-red-800",      label: "Rejected",         icon: XCircle },
  failed:       { bg: "bg-red-50",      text: "text-red-800",      label: "Failed",           icon: XCircle },
};

export default function RefundRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ booking_id: "", amount: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/my/refund-requests")
      .then(({ data }) => setRequests(data.requests || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Load failed"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const isOwner = user?.role === "hotel_partner"; // staff show different UI

  const submit = async () => {
    if (!form.booking_id.trim() || !form.amount || !form.reason.trim()) {
      return toast.error("All fields are required.");
    }
    setSaving(true);
    try {
      await api.post("/refund-requests", {
        booking_id: form.booking_id.trim(),
        amount: Number(form.amount),
        reason: form.reason.trim(),
      });
      toast.success(isOwner ? "Refund initiated" : "Refund request sent to Owner for approval");
      setOpenNew(false);
      setForm({ booking_id: "", amount: "", reason: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setSaving(false); }
  };

  const approve = async (rid) => {
    if (!window.confirm("Approve this refund? The full amount will be sent to the traveller via Razorpay.")) return;
    try {
      await api.post(`/refund-requests/${rid}/approve`);
      toast.success("Refund approved + processed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };

  const reject = async (rid) => {
    const reason = window.prompt("Reason for rejecting this refund?");
    if (!reason) return;
    try {
      await api.post(`/refund-requests/${rid}/reject`, { reason });
      toast.success("Refund rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="refund-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="hw-head text-3xl font-black text-slate-900">Refund Requests</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isOwner ? "Approve or reject refund requests from your Finance team." : "Raise refund requests. Owner approval required before Razorpay processes."}
          </p>
        </div>
        <Button onClick={() => setOpenNew(!openNew)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="new-refund-req-btn">
          <Plus className="w-4 h-4 mr-1" /> New refund request
        </Button>
      </div>

      {openNew && (
        <div className="hw-card p-6 mt-4" data-testid="new-refund-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="hw-label">Booking ID</Label>
              <Input value={form.booking_id} onChange={(e) => setForm({ ...form, booking_id: e.target.value })} placeholder="bkg_abc123" className="rounded-sm mt-1 font-mono" data-testid="rf-booking" />
            </div>
            <div>
              <Label className="hw-label">Amount (₹)</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="2500" className="rounded-sm mt-1" data-testid="rf-amount" />
            </div>
            <div className="md:col-span-1">
              <Label className="hw-label">Reason</Label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Guest medical emergency" className="rounded-sm mt-1" data-testid="rf-reason" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold" data-testid="rf-submit">
              {saving ? "Submitting…" : "Submit"}
            </Button>
            <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-sm">Cancel</Button>
          </div>
        </div>
      )}

      <div className="mt-6">
        {loading ? (
          <div className="hw-card p-10 text-center text-slate-500">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="hw-card p-10 text-center text-slate-500 italic">No refund requests yet.</div>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => {
              const s = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
              const Icon = s.icon;
              return (
                <div key={r.request_id} className={`hw-card p-5 border-l-4 border-l-emerald-500 ${s.bg}`} data-testid={`refund-req-${r.request_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`hw-chip ${s.text} bg-white`}><Icon className="w-3 h-3" /> {s.label}</span>
                        <span className="text-xs text-slate-500 font-mono">{r.request_id}</span>
                      </div>
                      <div className="mt-2 font-bold text-slate-900 flex items-center gap-2 text-lg">
                        <IndianRupee className="w-4 h-4" />
                        {Number(r.amount).toLocaleString("en-IN")}
                        <span className="text-slate-500 font-normal text-sm">· booking {r.booking_id}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">{r.reason}</p>
                      <div className="text-xs text-slate-500 mt-2">
                        By <b>{r.requested_by_name || "—"}</b> ({r.requested_by_role || "—"}) · {r.created_at?.slice(0, 16).replace("T", " ")}
                        {r.refund_id && <> · Razorpay ref <code className="bg-white px-1">{r.refund_id}</code></>}
                        {r.rejected_reason && <> · Reject reason: {r.rejected_reason}</>}
                      </div>
                    </div>
                    {isOwner && r.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approve(r.request_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid={`approve-${r.request_id}`}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(r.request_id)} className="rounded-sm border-red-300 text-red-700" data-testid={`reject-${r.request_id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
